import React, { useMemo, useState } from "react";
import { format } from "date-fns";
import toast from "react-hot-toast";
import TransactionStatusButton from "./TransactionStatusButton";
import TransactionNotes from "./TransactionNotes";

const API_BASE = (
  process.env.REACT_APP_API_URL || "http://localhost:5000"
).replace(/\/+$/, "");

const getFlagEmoji = (countryCode) => {
  if (!countryCode) return "🌍";
  return String(countryCode)
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt()));
};

const money = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  return n % 1 === 0 ? String(n) : String(Number(n.toFixed(2)));
};

const buildFullName = (user) => {
  const full = [user?.firstName, user?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return full || user?.email || "-";
};

const serviceLabel = (tx) => {
  const dest = tx?.destinationCountry || "destination";
  const method = String(tx?.method || "").toLowerCase();

  if (method.includes("mobile")) return `Dépôt Mobile Money — ${dest}`;
  if (method.includes("bank")) return `Dépôt bancaire — ${dest}`;
  if (method.includes("interac")) return `Transfert Interac — ${dest}`;
  return `Transfert — ${dest}`;
};

const Icon = ({ children }) => (
  <span className="inline-flex items-center justify-center w-5 h-5 text-gray-500 dark:text-gray-300 shrink-0">
    {children}
  </span>
);

const MailIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className="w-4 h-4"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M20 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V6a2 2 0 00-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
  </svg>
);

const PhoneIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className="w-4 h-4"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M6.6 10.8a15.1 15.1 0 006.6 6.6l2.2-2.2a1 1 0 011-.24c1.1.36 2.3.56 3.6.56a1 1 0 011 1V20a1 1 0 01-1 1C10.4 21 3 13.6 3 4a1 1 0 011-1h3.9a1 1 0 011 1c0 1.3.2 2.5.56 3.6a1 1 0 01-.24 1l-2.22 2.2z" />
  </svg>
);

const PinIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className="w-4 h-4"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M12 2a7 7 0 00-7 7c0 5.3 7 13 7 13s7-7.7 7-13a7 7 0 00-7-7zm0 9.5A2.5 2.5 0 1112 6a2.5 2.5 0 010 5.5z" />
  </svg>
);

const CalendarIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className="w-4 h-4"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M7 2h2v2h6V2h2v2h3a2 2 0 012 2v14a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2h3V2zm15 8H2v10h20V10z" />
  </svg>
);

export default function TransactionListCards({
  transactions,
  selected = [],
  toggleSelect,
  onStatusChange,
  onViewDetails,
  onViewMessages,
  onCancel,
  onNoteAdded,
}) {
  const items = useMemo(() => transactions || [], [transactions]);
  const [showNotesFor, setShowNotesFor] = useState(null);

  const safeCall = (fn, label) => {
    if (typeof fn !== "function") {
      toast.error(`Action "${label}" non disponible.`);
      return false;
    }
    return true;
  };

  if (!Array.isArray(items) || items.length === 0) {
    return (
      <p className="text-gray-500 text-center">Aucune transaction trouvée.</p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {items.map((tx) => {
        const id = tx?.id;
        const sid = String(id);
        const paymentRef = tx?.paymentRef || id;

        const flag = getFlagEmoji(
          tx?.destinationCountry || tx?.user?.profile?.countryCode,
        );

        const senderName = buildFullName(tx?.user);
        const senderEmail = tx?.user?.email || "-";
        const senderPhone =
          tx?.user?.phoneNumber || tx?.user?.profile?.phone || "-";
        const senderCountry =
          tx?.user?.profile?.country || tx?.user?.profile?.countryCode || "-";
        const senderAddress = tx?.user?.profile?.address || "";

        const receiverName =
          tx?.recipientName ||
          `${tx?.recipientFirstName || ""} ${tx?.recipientLastName || ""}`.trim() ||
          "-";

        const receiverEmail = tx?.recipientEmail || tx?.beneficiaryEmail || "-";
        const receiverPhone = tx?.recipientPhone || "-";
        const receiverCityOrCountry = tx?.destinationCountry || "-";

        const amountSent = tx?.amount;
        const fees = tx?.fee ?? 0;
        const receiveAmount = tx?.convertedAmount;
        const method = String(tx?.method || "").toUpperCase();
        const isBankTransfer =
          method === "BANK_TRANSFER" || method.includes("BANK");

        const receiverBankName =
          tx?.bankName ||
          tx?.bankDetails?.bankName ||
          tx?.beneficiaryDetails?.bankName ||
          tx?.beneficiary?.bankName ||
          "-";

        const receiverAccountNumber =
          tx?.accountNumber ||
          tx?.bankDetails?.accountNumber ||
          tx?.beneficiaryDetails?.accountNumber ||
          tx?.beneficiary?.accountNumber ||
          "-";
        const amountToPay =
          tx?.totalPay ?? Number(amountSent || 0) + Number(fees || 0);

        const createdAt = tx?.createdAt
          ? format(new Date(tx.createdAt), "dd/MM/yyyy HH:mm")
          : "-";

        const isSelected = selected.includes(sid);

        const noteCount = tx.noteCount ?? tx.notes?.length ?? 0;

        return (
          <div
            key={sid}
            className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-sm overflow-hidden"
          >
            <div className="flex flex-col lg:flex-row">
              <div className="lg:w-72 bg-teal-900 text-white p-6 flex items-center">
                <div className="text-xl sm:text-2xl font-semibold leading-snug">
                  {serviceLabel(tx)}
                </div>
              </div>

              <div className="flex-1 p-4 sm:p-5">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {
                        if (!safeCall(toggleSelect, "sélection")) return;
                        toggleSelect(sid);
                      }}
                      className="w-5 h-5"
                    />

                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xl">{flag}</span>
                      <span className="text-sm text-gray-700 dark:text-gray-200 truncate">
                        <span className="text-gray-500"># </span>
                        {paymentRef}
                      </span>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <TransactionStatusButton
                      transaction={tx}
                      onStatusChange={(transactionId, updatedTx) => {
                        if (!safeCall(onStatusChange, "changement de statut"))
                          return;
                        onStatusChange(transactionId, updatedTx);
                      }}
                    />
                  </div>
                </div>

                <div className="mt-3 border-t border-gray-100 dark:border-gray-700 pt-3">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2 md:gap-4 text-sm">
                    <div className="text-gray-800 dark:text-gray-100">
                      <span className="text-gray-500 dark:text-gray-400">
                        Montant à recevoir :
                      </span>{" "}
                      <strong>
                        {money(receiveAmount)} {tx?.recipientCurrency || ""}
                      </strong>
                    </div>

                    <div className="text-gray-800 dark:text-gray-100">
                      <span className="text-gray-500 dark:text-gray-400">
                        Montant envoyé :
                      </span>{" "}
                      <strong>
                        {money(amountSent)} {tx?.currency || ""}
                      </strong>
                    </div>

                    <div className="text-gray-800 dark:text-gray-100">
                      <span className="text-gray-500 dark:text-gray-400">
                        Frais :
                      </span>{" "}
                      <strong>
                        {money(fees)} {tx?.currency || ""}
                      </strong>
                    </div>

                    <div className="text-emerald-700 dark:text-emerald-300 md:text-right">
                      <span className="font-semibold">Total à payer :</span>{" "}
                      <strong>
                        {money(amountToPay)} {tx?.currency || ""}
                      </strong>
                    </div>
                  </div>
                </div>

                <div className="mt-3 border-t border-gray-100 dark:border-gray-700 pt-3 space-y-2 text-sm">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-gray-800 dark:text-gray-100 min-w-0">
                    <span className="font-semibold text-gray-500 dark:text-gray-400">
                      Bénéficiaire :
                    </span>

                    <span className="font-semibold">{receiverName}</span>

                    {isBankTransfer ? (
                      <>
                        <span className="inline-flex items-center gap-1 text-gray-700 dark:text-gray-200 min-w-0">
                          <span className="font-semibold">Banque :</span>
                          <span className="truncate whitespace-nowrap max-w-[220px] sm:max-w-[340px]">
                            {receiverBankName}
                          </span>
                        </span>

                        <span className="inline-flex items-center gap-1 text-gray-700 dark:text-gray-200 min-w-0">
                          <span className="font-semibold">Compte :</span>
                          <span className="truncate whitespace-nowrap max-w-[220px] sm:max-w-[340px]">
                            {receiverAccountNumber}
                          </span>
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="inline-flex items-center gap-1 text-gray-700 dark:text-gray-200 min-w-0">
                          <Icon>
                            <MailIcon />
                          </Icon>
                          <span className="truncate whitespace-nowrap max-w-[260px] sm:max-w-[520px]">
                            {receiverEmail}
                          </span>
                        </span>

                        <span className="inline-flex items-center gap-1 text-gray-700 dark:text-gray-200 min-w-0">
                          <Icon>
                            <PhoneIcon />
                          </Icon>
                          <span className="truncate whitespace-nowrap max-w-[220px] sm:max-w-[340px]">
                            {receiverPhone}
                          </span>
                        </span>
                      </>
                    )}

                    <span className="inline-flex items-center gap-1 text-gray-700 dark:text-gray-200 min-w-0">
                      <Icon>
                        <PinIcon />
                      </Icon>
                      <span className="truncate whitespace-nowrap max-w-[220px] sm:max-w-[340px]">
                        {receiverCityOrCountry}
                      </span>
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-gray-800 dark:text-gray-100 min-w-0">
                    <span className="font-semibold text-gray-500 dark:text-gray-400">
                      Expéditeur :
                    </span>
                    <span className="font-semibold">{senderName}</span>

                    <span className="inline-flex items-center gap-1 text-gray-700 dark:text-gray-200 min-w-0">
                      <Icon>
                        <MailIcon />
                      </Icon>
                      <span className="truncate whitespace-nowrap max-w-[260px] sm:max-w-[520px]">
                        {senderEmail}
                      </span>
                    </span>

                    <span className="inline-flex items-center gap-1 text-gray-700 dark:text-gray-200 min-w-0">
                      <Icon>
                        <PhoneIcon />
                      </Icon>
                      <span className="truncate whitespace-nowrap max-w-[220px] sm:max-w-[340px]">
                        {senderPhone}
                      </span>
                    </span>

                    <span className="inline-flex items-center gap-1 text-gray-700 dark:text-gray-200 min-w-0">
                      <Icon>
                        <PinIcon />
                      </Icon>
                      <span className="truncate whitespace-nowrap max-w-[260px] sm:max-w-[520px]">
                        {senderAddress || senderCountry}
                      </span>
                    </span>

                    {senderAddress && (
                      <span className="inline-flex items-center gap-1 text-gray-700 dark:text-gray-200 min-w-0">
                        <Icon>
                          <PinIcon />
                        </Icon>
                        <span className="truncate whitespace-nowrap max-w-[220px] sm:max-w-[340px]">
                          {senderCountry}
                        </span>
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                    <Icon>
                      <CalendarIcon />
                    </Icon>
                    <span className="font-semibold text-gray-500 dark:text-gray-400">
                      Créée le :
                    </span>
                    <span>{createdAt}</span>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (!safeCall(onViewDetails, "détails")) return;
                      onViewDetails(tx);
                    }}
                    className="px-4 py-2 rounded-md bg-amber-700 hover:bg-amber-800 text-white text-sm font-medium"
                  >
                    Détails
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setShowNotesFor(showNotesFor === sid ? null : sid)
                    }
                    className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium"
                  >
                    Mémo ({noteCount})
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      if (!safeCall(onViewMessages, "messages")) return;
                      onViewMessages(id);
                    }}
                    className="px-4 py-2 rounded-md bg-gray-900 hover:bg-black text-white text-sm font-medium dark:bg-gray-700 dark:hover:bg-gray-600"
                  >
                    Messages
                  </button>

                  {typeof onCancel === "function" && (
                    <button
                      type="button"
                      onClick={() => onCancel(id)}
                      className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm font-medium"
                    >
                      Annuler
                    </button>
                  )}
                </div>

                {showNotesFor === sid && (
                  <TransactionNotes
                    transactionId={id}
                    paymentRef={paymentRef}
                    apiUrl={API_BASE}
                    onClose={() => setShowNotesFor(null)}
                    onNoteAdded={() => {
                      if (typeof onNoteAdded === "function") {
                        onNoteAdded(id);
                      }
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
